import { portalFetch } from '../lib/apiClient';
import { usePortalAuthStore } from '../stores/portalAuthStore';

interface TranslationsResponse {
	languageId: number;
	languageCode: string;
	languageName: string;
	translations: Record<string, string>;
}

interface ActiveLanguage {
	id: number;
	code: string;
	name: string;
	isActive: boolean;
}

/**
 * Fetch translations for a language code via the portal API.
 * Used as the custom fetchTranslations callback for TranslationProvider.
 *
 * PD-15030 — works signed OUT too. This used to return an empty dictionary
 * whenever there was no token, so a signed-out shopper (or one whose session
 * had expired after they picked their language) saw the entire storefront
 * fall back to English keys — the sign-in page included. The dictionary is
 * UI copy, not customer data; the endpoint accepts anonymous requests and
 * `portalFetch` already supports a null token for anonymous browsing.
 */
export async function getTranslations(
	code: string,
	bustCache = false,
): Promise<Record<string, string>> {
	const token = usePortalAuthStore.getState().token;

	const url = bustCache
		? `/language/code/${code}?t=${Date.now()}`
		: `/language/code/${code}`;

	const response = await portalFetch<TranslationsResponse>(url, token);
	return response.translations;
}

/**
 * Get the user's preferred language from the portal auth store.
 * Used as the custom fetchPreferredLanguage callback for TranslationProvider.
 */
export async function getPreferredLanguage(): Promise<string> {
	const store = usePortalAuthStore.getState();

	// Check localStorage-persisted language preference first
	const storedLanguage = store.languagePreference;
	if (storedLanguage != null) {
		return storedLanguage;
	}

	// Fall back to browser language detection
	if (navigator.languages != null && navigator.languages.length > 0) {
		return navigator.languages[0].split('-')[0];
	}

	if (navigator.language != null) {
		return navigator.language.split('-')[0];
	}

	return 'en';
}

/**
 * Session-scoped cache for the active-language list. The tenant's
 * language set effectively never changes mid-session, but the
 * LanguageSwitcher unmounts/remounts with the menu drawer — without a
 * cache every drawer open refetched the list and the flag popped in
 * after a visible delay each time (PD-14607). The in-flight promise is
 * shared so concurrent mounts (header + drawer) trigger one request.
 */
let activeLanguagesCache: Array<ActiveLanguage> | null = null;
let activeLanguagesInFlight: Promise<Array<ActiveLanguage>> | null = null;

/**
 * Synchronous cache read — lets the LanguageSwitcher seed its initial
 * state and render the flag on first paint after the first load.
 */
export function getActiveLanguagesCached(): Array<ActiveLanguage> | null {
	return activeLanguagesCache;
}

/**
 * Fetch the list of active languages available for the portal.
 * Cached for the session after the first successful load.
 * Anonymous-capable (PD-15030) — the switcher renders signed out.
 */
export async function getActiveLanguages(
	token: string | null,
): Promise<Array<ActiveLanguage>> {
	if (activeLanguagesCache != null) {
		return activeLanguagesCache;
	}
	if (activeLanguagesInFlight != null) {
		return activeLanguagesInFlight;
	}
	activeLanguagesInFlight = portalFetch<{
		languages: Array<ActiveLanguage>;
	}>('/language', token)
		.then((response) => {
			activeLanguagesCache = response.languages;
			return response.languages;
		})
		.finally(() => {
			activeLanguagesInFlight = null;
		});
	return activeLanguagesInFlight;
}

/**
 * Update the user's language preference on the server.
 */
export async function updateLanguagePreference(
	token: string,
	languageCode: string,
): Promise<void> {
	await portalFetch<{ success: boolean }>('/language/preference', token, {
		method: 'PUT',
		body: JSON.stringify({ languageCode }),
	});
}
